/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import { type IntlShape, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import AiBotIcon from '@atlaskit/icon-lab/core/ai-bot';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { getAccentTokens } from '../../pm-plugins/decorations/colorSchemes/factory';
import { colorSchemeRegistry } from '../../pm-plugins/decorations/colorSchemes/schemes';
import type { ColorScheme } from '../../pm-plugins/decorations/colorSchemes/types';
import type { TagContributor } from '../../showDiffPluginType';

import { contributorTagMessages } from './messages';

// Floor for the tag width (~one 16px avatar plus padding), so the highlight-width clamp below
// cannot collapse the box on a very short highlight.
const MIN_TAG_WIDTH = '28px';

const styles = cssMap({
	// Positioned against the host widget its decoration renders on the change's first character:
	// `bottom: 100%` lifts the tag onto the line above, `inset-inline-start` starts it on that
	// character.
	constraint: {
		display: 'block',
		position: 'absolute',
		bottom: '100%',
		insetInlineStart: 0,
		minWidth: 0,
		// The tag may cover surrounding text, so it has to paint over it. Matches
		// `akEditorUnitZIndex`, inlined because imported values are banned inside `cssMap`.
		zIndex: 1,
		// It renders inside the document now, and it is not content: dragging a selection across the
		// change must not select it.
		userSelect: 'none',
	},
	tag: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		// Never wider than the highlight; overflow is ellipsised on the name below.
		maxWidth: '100%',
		minWidth: 0,
		overflow: 'hidden',
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		borderRadius: token('radius.small'),
		// `backgroundColor` and `borderBottomColor` come from the accent and are applied inline —
		// `cssMap` cannot take dynamic values.
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width.selected'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
	},
	hidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
	visible: {
		opacity: 1,
		pointerEvents: 'auto',
	},
	avatars: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	// Hand-rolled avatar stack: `@atlaskit/avatar-group` cannot render below 24px and the tag uses
	// 16px (`xxsmall`) avatars.
	leadingAvatar: {
		display: 'inline-flex',
		marginInlineEnd: token('space.negative.050'),
	},
	name: {
		display: 'block',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const getContributorName = (
	contributor: TagContributor,
	formatMessage: IntlShape['formatMessage'],
): string => {
	const name = contributor.name?.trim();
	if (name) {
		return name;
	}

	// `agentKind` defaults to `'external'` per the plugin type contract; an unidentified external
	// agent falls back to a generic label rather than rendering a nameless tag.
	if (contributor.kind === 'agent' && (contributor.agentKind ?? 'external') === 'external') {
		return formatMessage(contributorTagMessages.externalAgentName);
	}

	return '';
};

const ContributorAvatar = ({
	contributor,
	name,
	ringColor,
	stackIndex,
}: {
	contributor: TagContributor;
	name: string;
	ringColor: string;
	stackIndex?: number;
}): JSX.Element => {
	if (contributor.kind === 'user') {
		return (
			<Avatar
				appearance="circle"
				size="xxsmall"
				src={contributor.avatarUrl}
				name={name}
				stackIndex={stackIndex}
				borderColor={ringColor}
			/>
		);
	}

	return (
		<Avatar
			appearance="hexagon"
			size="xxsmall"
			src={contributor.avatarUrl}
			name={name}
			stackIndex={stackIndex}
			borderColor={ringColor}
		>
			{contributor.avatarUrl ? undefined : (
				<AvatarContent>
					{contributor.agentKind === 'rovo' ? (
						<RovoHexIcon size="xxsmall" label="" />
					) : (
						// `small` (12px) fits inside the 16px hexagon.
						<AiBotIcon label="" size="small" />
					)}
				</AvatarContent>
			)}
		</Avatar>
	);
};

type ContributorTagProps = {
	colorScheme?: ColorScheme;
	connectedContributor?: TagContributor;
	contributor: TagContributor;
	diffId: string;
	/**
	 * This editor's own content root (`editorView.dom`), read through the plugin's captured view.
	 *
	 * Every DOM lookup the tag makes is scoped to it, rather than resolved by class name from a
	 * highlight element: `.ProseMirror` is not this package's to rename, and with nested editors the
	 * nearest one is not necessarily the editor whose plugin rendered this tag.
	 *
	 * A getter rather than the element itself because the view arrives with the ProseMirror view
	 * lifecycle, which the plugin does not re-render for: reading it inside the effect means it is
	 * always the current one.
	 */
	getEditorRoot: () => HTMLElement | null;
	isActive?: boolean;
	isInserted?: boolean;
	/**
	 * Other decorations of the same change this one tag captions — the deleted half of a replacement.
	 * Hovering any of them reveals the tag; only `diffId` is measured against.
	 */
	linkedDiffIds?: string[];
};

/**
 * Resolves the tag's accent from the same scheme value and token maps the highlight used, so the two
 * can never drift.
 */
const getTagAccent = ({
	colorScheme,
	isActive,
	isInserted,
}: {
	colorScheme: ColorScheme | undefined;
	isActive: boolean;
	isInserted: boolean;
}): { background: string; border: string; text: string } => {
	const colors = colorSchemeRegistry[colorScheme ?? 'standard'];
	const accent = isInserted
		? isActive
			? colors.insertActiveColor
			: colors.insertColor
		: isActive
			? colors.deleteActiveColor
			: colors.deleteColor;

	return getAccentTokens(accent);
};

/** The line the change starts on: topmost of its fragments, leftmost among those. */
const findChangeStartLine = (highlights: Element[]): DOMRect | undefined => {
	let start: DOMRect | undefined;

	for (const highlight of highlights) {
		for (const rect of highlight.getClientRects()) {
			if (
				!start ||
				rect.top < start.top - 1 ||
				(Math.abs(rect.top - start.top) <= 1 && rect.left < start.left)
			) {
				start = rect;
			}
		}
	}

	return start;
};

/**
 * Observes the highlight this tag describes, for what CSS here cannot see: its hover state (the
 * highlight is a ProseMirror decoration in a different DOM subtree, so listeners are bound
 * imperatively via `data-diff-id`) and the width the tag is clamped to.
 *
 * Deliberately not routed through plugin state: a transaction per pointer move would re-run the
 * memoised decoration calculation.
 */
const useHighlightState = (
	diffId: string,
	highlightSelector: string,
	getEditorRoot: () => HTMLElement | null,
): { isHovered: boolean; maxWidthPx: number | undefined } => {
	const [isHovered, setIsHovered] = useState(false);
	const [maxWidthPx, setMaxWidthPx] = useState<number | undefined>(undefined);

	useEffect(() => {
		// Both queries are scoped to this editor's content root, so a tag can only ever bind to the
		// decorations its own plugin instance rendered — another editor on the page, or one nested
		// inside this one, cannot supply the geometry.
		const editorRoot = getEditorRoot();
		const highlights = editorRoot?.querySelectorAll(highlightSelector);
		if (!editorRoot || !highlights?.length) {
			return;
		}
		const elements = Array.from(highlights);
		// The width comes from this tag's OWN decoration; sibling halves of the change are here for
		// hover only.
		const ownElements = Array.from(editorRoot.querySelectorAll(`[data-diff-id="${diffId}"]`));

		const measure = () => {
			// Counted by distinct line box, not by client rect: an inline node inside the change (a
			// mention, emoji, date) splits the highlight into several rects on the same line, and
			// treating that as a wrap would drop the width clamp on changes that never wrapped.
			const lineTops = new Set<number>();
			for (const highlight of ownElements) {
				for (const rect of highlight.getClientRects()) {
					lineTops.add(Math.round(rect.top));
				}
			}

			// A wrapped change's first line says nothing useful about how wide the tag may be.
			const width = lineTops.size > 1 ? undefined : findChangeStartLine(ownElements)?.width;
			setMaxWidthPx(width);
		};
		measure();

		// An inline element cannot be observed itself (ResizeObserver skips boxes with no principal
		// box), so the block it sits in is observed instead.
		const resizeObserver = new ResizeObserver(measure);
		for (const parent of new Set(ownElements.map(({ parentElement }) => parentElement))) {
			if (parent) {
				resizeObserver.observe(parent);
			}
		}

		// `bindAll` takes a single target, and a change can render more than one highlight.
		const unbinds = elements.map((highlight) =>
			bindAll(highlight, [
				{ type: 'mouseenter', listener: () => setIsHovered(true) },
				{ type: 'mouseleave', listener: () => setIsHovered(false) },
			]),
		);

		return () => {
			resizeObserver.disconnect();
			unbinds.forEach((unbind) => unbind());
		};
	}, [diffId, getEditorRoot, highlightSelector]);

	return { isHovered, maxWidthPx };
};

/**
 * A small tag naming who made one diff, rendered into the host widget its decoration places on the
 * change's first character and positioned above it.
 */
export function ContributorTag({
	colorScheme,
	connectedContributor,
	contributor,
	diffId,
	getEditorRoot,
	isActive,
	isInserted = true,
	linkedDiffIds,
}: ContributorTagProps): JSX.Element {
	const { formatMessage } = useIntl();
	// A plain string, so the hook's effect is not re-run by a fresh array on every render.
	const highlightSelector = [diffId, ...(linkedDiffIds ?? [])]
		.map((id) => `[data-diff-id="${id}"]`)
		.join(', ');
	const { isHovered: isHighlightHovered, maxWidthPx } = useHighlightState(
		diffId,
		highlightSelector,
		getEditorRoot,
	);
	const accent = getTagAccent({ colorScheme, isActive: Boolean(isActive), isInserted });

	const nameRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const [isHoveredOrFocused, setIsHoveredOrFocused] = useState(false);

	const isConnectedPair = Boolean(connectedContributor);
	const isPrimaryAgent = contributor.kind === 'agent';

	const primaryName = getContributorName(contributor, formatMessage);
	const secondaryName = connectedContributor
		? getContributorName(connectedContributor, formatMessage)
		: '';

	// Resolved by `kind` so a reverse `connectedTo` (an attributed user pointing at its agent)
	// renders identically.
	const agentName = isPrimaryAgent ? primaryName : secondaryName;
	const userName = isPrimaryAgent ? secondaryName : primaryName;
	const userContributor = isPrimaryAgent ? connectedContributor : contributor;
	const agentContributor = isPrimaryAgent ? contributor : connectedContributor;

	const displayName = isConnectedPair ? agentName || primaryName : primaryName;

	const fullLabel = isConnectedPair
		? formatMessage(contributorTagMessages.changedByConnected, { agentName, userName })
		: formatMessage(contributorTagMessages.changedBy, { name: primaryName });

	const measureTruncation = useCallback(() => {
		const element = nameRef.current;
		if (element) {
			setIsTruncated(element.scrollWidth > element.clientWidth);
		}
	}, []);

	const isVisible = Boolean(isActive) || isHighlightHovered || isHoveredOrFocused;

	// A connected pair only ever renders the agent name, so it always gets a tooltip; a single
	// contributor only when its name is clipped.
	const tooltipContent = isTruncated || isConnectedPair ? fullLabel : null;

	return (
		<span
			css={styles.constraint}
			style={{
				// Clamped to the highlight's own width, floored at `MIN_TAG_WIDTH` so a one-character
				// change still shows an avatar.
				maxWidth:
					maxWidthPx === undefined
						? undefined
						: `max(${Math.round(maxWidthPx)}px, ${MIN_TAG_WIDTH})`,
			}}
		>
			<Tooltip
				content={tooltipContent}
				// A tooltip below would cover the highlighted text the tag describes.
				position="top"
				// The label is already exposed by the visually hidden text below.
				isScreenReaderAnnouncementDisabled
			>
				{(tooltipProps) => (
					<span
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...tooltipProps}
						data-testid="diff-contributor-tag"
						// Deliberately no `aria-label`: it would be announced ahead of the element's
						// contents, but design requires the contributor *after* the change — hence the
						// visually hidden last child.
						role="note"
						// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, @atlassian/a11y/no-noninteractive-tabindex
						tabIndex={0}
						css={[styles.tag, isVisible ? styles.visible : styles.hidden]}
						style={{ backgroundColor: accent.background, borderBottomColor: accent.border }}
						onMouseOver={(event) => {
							tooltipProps.onMouseOver(event);
							measureTruncation();
							setIsHoveredOrFocused(true);
						}}
						onMouseOut={(event) => {
							tooltipProps.onMouseOut(event);
							setIsHoveredOrFocused(false);
						}}
						onFocus={(event) => {
							tooltipProps.onFocus(event);
							measureTruncation();
							setIsHoveredOrFocused(true);
						}}
						onBlur={(event) => {
							tooltipProps.onBlur(event);
							setIsHoveredOrFocused(false);
						}}
					>
						<span css={styles.avatars}>
							{isConnectedPair && userContributor ? (
								<span css={styles.leadingAvatar}>
									<ContributorAvatar
										contributor={userContributor}
										name={userName}
										ringColor={accent.background}
										stackIndex={1}
									/>
								</span>
							) : null}
							{isConnectedPair && agentContributor ? (
								<ContributorAvatar
									contributor={agentContributor}
									name={agentName}
									ringColor={accent.background}
									stackIndex={0}
								/>
							) : null}
							{!isConnectedPair ? (
								<ContributorAvatar
									contributor={contributor}
									name={primaryName}
									ringColor={accent.background}
								/>
							) : null}
						</span>
						<span
							css={styles.name}
							data-testid="diff-contributor-tag-name"
							ref={nameRef}
							style={{ color: accent.text }}
						>
							{displayName}
						</span>
						{/* Must stay the LAST child: screen readers walk the tag in DOM order. */}
						<VisuallyHidden>{fullLabel}</VisuallyHidden>
					</span>
				)}
			</Tooltip>
		</span>
	);
}
